// /billing/success?session_id=... — polls the backend for payment
// status after Stripe redirects the user back. The polling loop is
// the safety net that ensures the subscription is flipped to
// "active" even if the webhook is delayed.
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { api } from '@/pages/planning/_helpers';
import { useAuth } from '@/context/AuthContext';

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2500;

export default function BillingSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshEntitlements } = useAuth();
  const [phase, setPhase] = useState('checking');  // checking | success | expired | timeout | error
  const [detail, setDetail] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get('session_id');
    if (!sid) { setPhase('error'); setDetail('Missing session_id'); return; }

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const r = await api(`/payments/checkout/status/${sid}`);
        setDetail(r);
        if (r.payment_status === 'paid') {
          setPhase('success');
          await refreshEntitlements();
          return;
        }
        if (r.status === 'expired') { setPhase('expired'); return; }
        if (attempts.current >= MAX_ATTEMPTS) { setPhase('timeout'); return; }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        setPhase('error'); setDetail(e.message);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [location.search, refreshEntitlements]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="billing-success-page">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 text-center space-y-4">
          {phase === 'checking' && (
            <>
              <Loader2 className="h-10 w-10 animate-spin mx-auto text-muted-foreground" />
              <h1 className="text-xl font-display">Confirming your payment…</h1>
              <p className="text-sm text-muted-foreground">Stripe is finalising the charge. This usually takes a few seconds.</p>
            </>
          )}
          {phase === 'success' && (
            <>
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
              <h1 className="text-2xl font-display">Subscription activated!</h1>
              <p className="text-sm text-muted-foreground">Trial limits are now lifted across your company. Welcome aboard.</p>
              <div className="text-xs text-muted-foreground tabular-nums" data-testid="billing-success-amount">
                {detail?.amount_total ? `${(detail.amount_total / 100).toFixed(2)} ${detail.currency?.toUpperCase()}` : ''}
              </div>
              <Button asChild className="w-full" data-testid="billing-success-back">
                <Link to="/company-admin">Back to company admin</Link>
              </Button>
            </>
          )}
          {phase === 'expired' && (
            <>
              <Clock className="h-10 w-10 mx-auto text-amber-500" />
              <h1 className="text-xl font-display">Session expired</h1>
              <p className="text-sm text-muted-foreground">The Stripe checkout session timed out. Please try again.</p>
              <Button onClick={() => navigate('/company-admin')} className="w-full">Back to company admin</Button>
            </>
          )}
          {phase === 'timeout' && (
            <>
              <Clock className="h-10 w-10 mx-auto text-amber-500" />
              <h1 className="text-xl font-display">Still processing</h1>
              <p className="text-sm text-muted-foreground">Stripe is taking longer than usual. You can safely close this page — once the payment confirms, your subscription will activate automatically.</p>
              <Button onClick={() => navigate('/company-admin')} className="w-full">Back to company admin</Button>
            </>
          )}
          {phase === 'error' && (
            <>
              <XCircle className="h-10 w-10 mx-auto text-rose-500" />
              <h1 className="text-xl font-display">Something went wrong</h1>
              <p className="text-sm text-muted-foreground">{detail || 'Unexpected error'}</p>
              <Button onClick={() => navigate('/company-admin')} className="w-full">Back to company admin</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
